import React, { useMemo, useState } from "react";
import TabSelector from "./commonComponents/TabSelector";
import Accordian from "./commonComponents/AccordianWithStyle";
import DisplayAllCharges from "./DisplayAllCharges";
import Checkbox from "../../components/CheckBox";
import CustomInputBox from "../../components/Input";
import { capitalizeFirstLetter } from "../../utils/utility";

const tabs = [
  { id: "rateCard", label: "Rate Card" },
  { id: "allCharges", label: "All Charges" },
];

type B2BMatrixRow = {
  fromRegion?: string;
  cells?: Array<{ toRegion?: string; rate?: number }>;
};

type RegionRateConfig = {
  transitType?: string;
  minimumWeightCharge?: number;
  matrix?: B2BMatrixRow[];
};

type B2BServiceMatrix = {
  courierServiceId?: number;
  partnerName?: string;
  partnerServiceId?: string;
  partnerServiceName?: string;
  mode?: string;
  serviceType?: string;
  regionRateConfigs?: RegionRateConfig[];
  /** COD on each partner row (flat and/or nested like rate-card API) */
  isCodHandling?: boolean;
  codPercentage?: number | string;
  codCharges?: number | string;
  codFixedAmount?: number | string;
  codChargePreference?: string;
  codRefundEnabled?: boolean;
  /** GraphQL / alternate flat names on partner row */
  percentageCharge?: number | string;
  fixedAmount?: number | string;
  condition?: string;
  enableCodRefund?: boolean;
  codHandling?: {
    percentageCharge?: number | string | null;
    fixedAmount?: number | string | null;
    condition?: string | null;
    isEnabled?: boolean | null;
    enableCODRefund?: boolean | null;
  };
  /** Per-partner divisor when API puts it on the matrix row */
  divisor?: number | string | null;
  zoneOptions?: Array<{ divisor?: number | string | null }>;
};

/** Build column order and cell grid from API-style matrix rows. */
function buildRateMatrixTable(matrix: B2BMatrixRow[] | null | undefined): {
  rowLabels: string[];
  colLabels: string[];
  cells: (number | string)[][];
} {
  const rows = (Array.isArray(matrix) ? matrix : []).filter(
    (r) => String(r?.fromRegion ?? "").trim() !== "",
  );
  const rowLabels = rows.map((r) => String(r?.fromRegion ?? "").trim());
  const colOrder: string[] = [];
  const seenCol = new Set<string>();
  for (const row of rows) {
    for (const c of row.cells || []) {
      const to = String(c?.toRegion ?? "").trim();
      if (!to || seenCol.has(to)) continue;
      seenCol.add(to);
      colOrder.push(to);
    }
  }
  const cells = rows.map((row) => {
    const byTo = new Map<string, number>();
    for (const c of row.cells || []) {
      const to = String(c?.toRegion ?? "").trim();
      if (!to) continue;
      const rate = c?.rate;
      byTo.set(to, typeof rate === "number" && !Number.isNaN(rate) ? rate : 0);
    }
    return colOrder.map((col) => {
      if (!byTo.has(col)) return "—";
      return byTo.get(col) as number;
    });
  });
  return { rowLabels, colLabels: colOrder, cells };
}

/** FORWARD before RETURN, then other transit types. */
function orderRegionConfigs(configs: RegionRateConfig[]): RegionRateConfig[] {
  const list = [...configs];
  const rank = (t: string) => {
    const u = t.toUpperCase();
    if (u === "FORWARD") return 0;
    if (u === "RETURN") return 1;
    return 2;
  };
  list.sort((a, b) => {
    const ra = rank(String(a.transitType ?? ""));
    const rb = rank(String(b.transitType ?? ""));
    return ra - rb;
  });
  return list;
}

/** Read-only minimum weight row — matches RateManagementB2B. */
function MinimumWeightChargeSummaryReadOnly({
  label,
  amount,
  namePrefix,
}: {
  label: string;
  amount: number | string | null | undefined;
  namePrefix: string;
}) {
  const hasValue = amount != null && String(amount).trim() !== "";
  const str = hasValue ? String(amount) : "";
  return (
    <div className="rounded-xl bg-gray-50 p-4 flex flex-wrap items-center gap-3">
      <Checkbox checked={hasValue} disabled={true} onChange={() => {}} />
      <span className="text-sm text-gray-700 min-w-[165px]">{label}</span>
      <CustomInputBox
        label="Amount"
        inputType="text"
        value={str}
        isDisabled={true}
        onChange={() => {}}
        className="w-[140px]"
        inputClassName="w-[140px] !h-[30px] !rounded-xl !px-3 !py-2 !border !border-gray-300 !text-sm"
        name={`${namePrefix}-minw`}
      />
    </div>
  );
}

/** Table markup aligned with RateManagementB2B summary (read-only). */
function B2BRateMatrixTable({
  rowLabels,
  colLabels,
  cells,
  title,
}: {
  rowLabels: string[];
  colLabels: string[];
  cells: (number | string)[][];
  title: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 overflow-x-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-[18px] font-semibold text-gray-800">{title}</h4>
        </div>
      </div>
      <table className="min-w-full text-sm border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left z-10">
              From / To
            </th>
            {colLabels.map((col) => (
              <th
                key={col}
                className="bg-gray-50 px-4 py-2 text-left whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowLabels.map((rowLabel, rIdx) => (
            <tr key={`${rowLabel}-${rIdx}`}>
              <td className="sticky left-0 bg-white border-t px-3 py-2 font-medium whitespace-nowrap z-10">
                {rowLabel}
              </td>
              {(cells[rIdx] || []).map((cell, cIdx) => (
                <td
                  key={`${rowLabel}-${colLabels[cIdx]}`}
                  className="border-t px-4 py-2 text-gray-700"
                >
                  {cell === "—" ? "—" : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function mapChargeNameToChargeType(chargeName: string): string {
  const normalized = chargeName.trim().toLowerCase();
  const direct: Record<string, string> = {
    "appointment-based delivery charges": "appointment",
    "fuel surcharge": "fuel_surcharge",
    "currency adjustment charges": "currency_adjustment_charge",
    "currency adjustment charge": "currency_adjustment_charge",
    "out of delivery area (oda)": "oda",
    "out of pickup area (opa)": "opa",
    "green tax charges (delhi-ncr)": "green_tax",
    "kerala tax": "kerala_tax",
    "reverse pickup": "reverse_pickup",
    "docket charges": "misc_docket",
    "fov/rov charges": "fov_rov",
    "demurrage charges": "demurrage",
    "reattempt charge": "reattempt_charge",
    "holiday pickup/delivery charges": "holiday",
    "peak surcharges": "peak",
    "handling charges (heavy shipment)": "handling_charge",
    multistory: "multistory",
    pod: "pod",
    "to-pay charges": "topay",
    "to pay charges": "topay",
  };
  if (direct[normalized]) return direct[normalized];
  if (normalized.includes("appointment")) return "appointment";
  if (normalized.includes("fuel")) return "fuel_surcharge";
  if (normalized.includes("handling")) return "handling_charge";
  return normalized.replace(/\s+/g, "_").replace(/-/g, "_");
}

function variablesDataToSummaryCharges(data: unknown): any[] {
  if (!Array.isArray(data)) return [];
  const out: any[] = [];
  for (const entry of data) {
    if (!entry || typeof entry !== "object") continue;
    const rec = entry as Record<string, unknown>;
    const isActive = rec.isActive;
    if (isActive === false) continue;
    for (const [key, value] of Object.entries(rec)) {
      if (key === "isActive") continue;
      if (!value || typeof value !== "object") continue;
      const chargeName = key;
      const chargeType = mapChargeNameToChargeType(chargeName);
      out.push({
        chargeType,
        chargeName,
        ...(value as Record<string, unknown>),
      });
    }
  }
  return out;
}

/** Plan-level COD on `planInfo.data` — merged per partner when matrix rows omit COD */
export type PlanCodFallback = {
  codCharges?: number | string | null;
  codPercentage?: number | string | null;
  codChargePreference?: string | null;
  codRefundEnabled?: boolean | null;
  isCodHandling?: boolean | null;
};

type ViewSummaryRateCardB2BProps = {
  businessType?: string;
  b2bRegionRateMatrix?: B2BServiceMatrix[] | null;
  variablesData?: unknown;
  planCodFallback?: PlanCodFallback | null;
};

function mergePartnerCodWithPlan(
  s: B2BServiceMatrix,
  plan: PlanCodFallback | null | undefined,
): B2BServiceMatrix {
  if (!plan || typeof plan !== "object") return s;
  return {
    ...s,
    isCodHandling: s.isCodHandling ?? plan.isCodHandling ?? undefined,
    codPercentage: s.codPercentage ?? plan.codPercentage ?? undefined,
    codCharges: s.codCharges ?? plan.codCharges ?? undefined,
    codChargePreference:
      s.codChargePreference ?? plan.codChargePreference ?? undefined,
    codRefundEnabled: s.codRefundEnabled ?? plan.codRefundEnabled ?? undefined,
  };
}

function normalizeCodCondition(value: string | null | undefined): string {
  if (value == null || String(value).trim() === "") return "";
  const v = String(value).trim().toUpperCase();
  if (v === "HIGHER") return "Higher";
  if (v === "LOWER") return "Lower";
  return value.charAt(0).toUpperCase() + String(value).slice(1).toLowerCase();
}

function conditionFromCodApi(condition?: string | null): string {
  if (condition == null || String(condition).trim() === "") return "";
  const u = String(condition).trim().toUpperCase();
  if (u === "WHICHEVER_HIGHER" || u === "HIGHER") return "Higher";
  if (u === "WHICHEVER_LOWER" || u === "LOWER") return "Lower";
  return normalizeCodCondition(condition);
}

function serviceCodHasData(s: B2BServiceMatrix): boolean {
  const ch = s.codHandling;
  return (
    s.isCodHandling === true ||
    s.codPercentage != null ||
    s.codCharges != null ||
    s.codFixedAmount != null ||
    s.percentageCharge != null ||
    s.fixedAmount != null ||
    (s.codChargePreference != null &&
      String(s.codChargePreference).trim() !== "") ||
    (s.condition != null && String(s.condition).trim() !== "") ||
    s.codRefundEnabled === true ||
    s.enableCodRefund === true ||
    ch?.isEnabled === true ||
    ch?.percentageCharge != null ||
    ch?.fixedAmount != null ||
    (ch?.condition != null && String(ch.condition).trim() !== "") ||
    ch?.enableCODRefund === true
  );
}

/** Read-only COD row — matches RateManagementB2B summary layout. */
function CodHandlingSummaryReadOnly({
  codEnabled,
  codPercentage,
  codFixedAmount,
  codCondition,
  codRefundEnabled,
  namePrefix = "b2b-summary-cod",
}: {
  codEnabled: boolean;
  codPercentage: string;
  codFixedAmount: string;
  codCondition: string;
  codRefundEnabled: boolean;
  namePrefix?: string;
}) {
  return (
    <div className="rounded-xl bg-gray-50 p-4 flex flex-wrap items-center gap-3 mb-4">
      <Checkbox checked={codEnabled} disabled={true} onChange={() => {}} />
      <span className="text-sm text-gray-700 min-w-[110px]">COD Handling</span>
      <CustomInputBox
        label="Percentage Charge %"
        inputType="text"
        value={codPercentage}
        isDisabled={true}
        onChange={() => {}}
        className="w-[140px]"
        inputClassName="w-[140px] !h-[30px] !rounded-xl !px-3 !py-2 !border !border-gray-300 !text-sm"
        name={`${namePrefix}-percentage`}
      />
      <CustomInputBox
        label="Fix Amount"
        inputType="text"
        value={codFixedAmount}
        isDisabled={true}
        onChange={() => {}}
        className="w-[140px]"
        inputClassName="w-[140px] !h-[30px] !rounded-xl !px-3 !py-2 !border !border-gray-300 !text-sm"
        name={`${namePrefix}-fixed`}
      />
      <CustomInputBox
        label="Condition"
        inputType="text"
        value={codCondition}
        onChange={() => {}}
        className="w-[140px]"
        inputClassName="w-[140px] !h-[30px] !rounded-xl !px-3 !py-2 !border !border-gray-300 !text-sm"
        name={`${namePrefix}-condition`}
        isDisabled
      />
      {/* <div className="flex items-center gap-2">
        <Checkbox
          checked={codRefundEnabled}
          disabled={true}
          onChange={() => {}}
        />
        <span className="text-sm text-gray-700">Enable COD Refund</span>
      </div> */}
    </div>
  );
}

function codValuesFromService(s: B2BServiceMatrix): {
  codEnabled: boolean;
  codPercentage: string;
  codFixedAmount: string;
  codCondition: string;
  codRefundEnabled: boolean;
} {
  const ch = s.codHandling;
  const pct =
    s.codPercentage != null && String(s.codPercentage) !== ""
      ? String(s.codPercentage)
      : s.percentageCharge != null && String(s.percentageCharge) !== ""
        ? String(s.percentageCharge)
        : ch?.percentageCharge != null && String(ch.percentageCharge) !== ""
          ? String(ch.percentageCharge)
          : "";
  const fixedRaw =
    s.codFixedAmount != null && String(s.codFixedAmount) !== ""
      ? String(s.codFixedAmount)
      : s.fixedAmount != null && String(s.fixedAmount) !== ""
        ? String(s.fixedAmount)
        : s.codCharges != null && String(s.codCharges) !== ""
          ? String(s.codCharges)
          : ch?.fixedAmount != null && String(ch.fixedAmount) !== ""
            ? String(ch.fixedAmount)
            : "";
  const condFromPreference =
    s.codChargePreference != null && String(s.codChargePreference).trim() !== ""
      ? normalizeCodCondition(s.codChargePreference)
      : "";
  const condFromRoot =
    s.condition != null && String(s.condition).trim() !== ""
      ? conditionFromCodApi(s.condition)
      : "";
  const cond =
    condFromPreference ||
    condFromRoot ||
    conditionFromCodApi(ch?.condition ?? undefined);
  const codEnabled =
    s.isCodHandling === true ||
    ch?.isEnabled === true ||
    pct !== "" ||
    fixedRaw !== "" ||
    (s.codChargePreference != null &&
      String(s.codChargePreference).trim() !== "") ||
    (s.condition != null && String(s.condition).trim() !== "") ||
    (ch?.condition != null && String(ch.condition).trim() !== "");
  const codRefundEnabled =
    Boolean(s.codRefundEnabled) ||
    Boolean(s.enableCodRefund) ||
    Boolean(ch?.enableCODRefund);
  return {
    codEnabled,
    codPercentage: pct,
    codFixedAmount: fixedRaw,
    codCondition: cond,
    codRefundEnabled,
  };
}

/** Same resolution order as RateCardSummaryComponent / RateManagementB2B (row → zone option → plan). */
function divisorForPartner(
  service: B2BServiceMatrix,
  planLevelDivisor: string | number | null | undefined,
): string | null {
  const raw =
    service.divisor ??
    service.zoneOptions?.[0]?.divisor ??
    planLevelDivisor ??
    null;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw);
}

/** Outer accordion header — matches RateCardSummaryComponent `renderHeaderContent` pills + divisor when open. */
function PartnerAccordionHeader({
  service,
  businessType: bt,
  isOpen,
  divisorDisplay,
}: {
  service: B2BServiceMatrix;
  businessType?: string;
  isOpen: boolean;
  divisorDisplay: string | null;
}) {
  const displayName =
    service.partnerName ||
    service.partnerServiceName ||
    `Partner ${service.courierServiceId ?? ""}`;
  return (
    <div className="flex w-full justify-between items-center gap-3 flex-wrap">
      <div className="flex items-center font-Open font-semibold gap-2 min-w-0 flex-wrap">
        <h3>{capitalizeFirstLetter(displayName)}</h3>
        <span className="rounded-full bg-[#D0EBFF] text-black px-3 py-0.5 text-sm font-medium border border-[#91CAFF]">
          {(bt || "B2B").toUpperCase()}
        </span>
        <span className="rounded-full bg-[#E8E8E8] text-black px-3 py-0.5 text-sm font-medium border border-[#A4A4A4]">
          {capitalizeFirstLetter(service.mode || "")}
        </span>
        <span className="rounded-full bg-[#494949] text-white px-3 py-0.5 text-sm font-medium border border-[#323232]">
          {capitalizeFirstLetter(service.serviceType || "Standard")}
        </span>
      </div>
      {/* {isOpen && divisorDisplay ? (
        <div className="flex items-center gap-2 mr-1 shrink-0">
          <span className="text-sm text-gray-600 font-medium">Divisor:</span>
          <input
            type="text"
            value={divisorDisplay}
            disabled
            readOnly
            className="w-20 px-3 py-1 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700"
          />
        </div>
      ) : null} */}
    </div>
  );
}

const ViewSummaryRateCardB2B = ({
  businessType,
  b2bRegionRateMatrix = [],
  variablesData,
  planCodFallback = null,
  divisor: planLevelDivisor = null,
}: {
  businessType?: string;
  b2bRegionRateMatrix?: B2BServiceMatrix[] | null;
  variablesData?: unknown;
  planCodFallback?: PlanCodFallback | null;
  /** Plan-level fallback when matrix rows omit divisor (from `planInfo.data.divisor`) */
  divisor?: string | number | null;
}) => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [openPartnerIndex, setOpenPartnerIndex] = useState<number | null>(null);

  const matrixList = useMemo(
    () => (Array.isArray(b2bRegionRateMatrix) ? b2bRegionRateMatrix : []),
    [b2bRegionRateMatrix],
  );

  const summaryVariableCharges = useMemo(
    () => variablesDataToSummaryCharges(variablesData),
    [variablesData],
  );

  const hasPartners = matrixList.length > 0;
  const hasVariables = summaryVariableCharges.length > 0;

  if (!hasPartners && !hasVariables) {
    return (
      <div className="p-4 text-gray-600 w-full flex justify-center items-center min-h-[200px]">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">No B2B summary data</p>
          <p className="text-sm text-gray-500">
            Region rate matrix and variable charges are empty.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-Open">
      <TabSelector
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <div className="mt-2 p-2 pb-24 rounded-md">
        {activeTab === "rateCard" && (
          <div>
            {!hasPartners ? (
              <p className="text-sm text-gray-500">No region rate matrix.</p>
            ) : (
              <>
                <div className="mb-4"></div>
                <div>
                  {matrixList.map((service, partnerIndex) => {
                    const codSource = mergePartnerCodWithPlan(
                      service,
                      planCodFallback,
                    );
                    const outerKey = `${service.courierServiceId}-${service.partnerServiceId ?? partnerIndex}`;
                    const transitSections = orderRegionConfigs(
                      service.regionRateConfigs || [],
                    )
                      .map((cfg, cfgIdx) => {
                        const { rowLabels, colLabels, cells } =
                          buildRateMatrixTable(cfg.matrix);
                        if (colLabels.length === 0 || rowLabels.length === 0) {
                          return null;
                        }
                        const transitRaw =
                          String(cfg.transitType ?? "").toUpperCase() || "";
                        const cardTitle =
                          transitRaw === "RETURN"
                            ? "Return Rate Card"
                            : transitRaw === "FORWARD"
                              ? "Forward Rate Card"
                              : `Rate Card (${cfg.transitType || cfgIdx + 1})`;
                        const minLabel =
                          transitRaw === "RETURN"
                            ? "Minimum Weight Charge (Return)"
                            : transitRaw === "FORWARD"
                              ? "Minimum Weight Charge (Forward)"
                              : `Minimum weight charge (${cfg.transitType || "Transit"})`;
                        return {
                          key: `${transitRaw || "t"}-${cfgIdx}`,
                          cardTitle,
                          minLabel,
                          minW: cfg.minimumWeightCharge,
                          rowLabels,
                          colLabels,
                          cells,
                        };
                      })
                      .filter(Boolean) as Array<{
                      key: string;
                      cardTitle: string;
                      minLabel: string;
                      minW?: number;
                      rowLabels: string[];
                      colLabels: string[];
                      cells: (number | string)[][];
                    }>;

                    return (
                      <Accordian
                        key={outerKey}
                        accordionKey={outerKey}
                        headerContent={
                          <PartnerAccordionHeader
                            service={service}
                            businessType={businessType}
                            isOpen={openPartnerIndex === partnerIndex}
                            divisorDisplay={divisorForPartner(
                              service,
                              planLevelDivisor,
                            )}
                          />
                        }
                        innerContent={
                          openPartnerIndex === partnerIndex ? (
                            <div className="h-[600px] max-h-none z-20 overflow-y-scroll pr-2">
                              {service.partnerServiceName &&
                              service.partnerName &&
                              service.partnerServiceName !==
                                service.partnerName ? (
                                <p className="text-xs text-gray-500 mb-3 px-1">
                                  {service.partnerServiceName}
                                  {service.partnerServiceId
                                    ? ` · ${service.partnerServiceId}`
                                    : ""}
                                </p>
                              ) : null}
                              {serviceCodHasData(codSource) ? (
                                <CodHandlingSummaryReadOnly
                                  {...codValuesFromService(codSource)}
                                  namePrefix={`b2b-cod-${service.courierServiceId ?? partnerIndex}`}
                                />
                              ) : null}
                              {transitSections.length === 0 ? (
                                <p className="text-sm text-gray-500 px-2 py-4">
                                  No rate matrix rows for this partner.
                                </p>
                              ) : (
                                <div className="space-y-6 pb-3">
                                  {transitSections.map((sec) => (
                                    <div key={sec.key} className="space-y-4">
                                      <MinimumWeightChargeSummaryReadOnly
                                        label={sec.minLabel}
                                        amount={sec.minW}
                                        namePrefix={`b2b-mw-${service.courierServiceId ?? partnerIndex}-${sec.key}`}
                                      />
                                      <B2BRateMatrixTable
                                        title={sec.cardTitle}
                                        rowLabels={sec.rowLabels}
                                        colLabels={sec.colLabels}
                                        cells={sec.cells}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : null
                        }
                        isOpen={openPartnerIndex === partnerIndex}
                        onToggle={() => {
                          if (openPartnerIndex === partnerIndex) {
                            setOpenPartnerIndex(null);
                            return;
                          }
                          setOpenPartnerIndex(partnerIndex);
                        }}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "allCharges" && (
          <div>
            {!hasVariables ? (
              <p className="text-sm text-gray-500">No variable charges.</p>
            ) : (
              <DisplayAllCharges
                businessType={businessType}
                summaryVariableCharges={summaryVariableCharges}
                isSummary={true}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewSummaryRateCardB2B;
